import React, { Fragment, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'react-apollo';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import {
  change,
  destroy,
  Field,
  FormSection,
  formValueSelector,
  getFormValues,
  reduxForm,
} from 'redux-form';
import cn from 'classnames';
import { get, isEmpty, some, isNil } from 'lodash';
import FormControlLabel from '@material-ui/core/FormControlLabel';

import { toast } from 'chameleon/ui-stack/ui/toast';
import Icon from 'chameleon/ui-stack/ui/base/icon';
import { Icon as LegacyIcon, LoadingSpinner } from 'legacy-packages/ft-cb';
import {
  generateAnswerSheets as generateASAction,
  generateAnswerSheetsPreview as generateAnswerSheetsPreviewAction,
  resetAnswerSheetsPreview as resetAnswerSheetsPreviewAction,
} from 'legacy/src/actions/paperscorer';
import Checkbox from 'legacy/src/components/Checkbox';
import DownloadAnswerSheetTitle from 'legacy/src/components/paperscorer/DownloadAnswerSheetTitle';
import { testBookletQuery } from 'chameleon-src/queries';
import FRQPrintSettings from 'legacy/src/components/paperscorer/PrintSettings';
import ClickableText from 'legacy/src/utility/ClickableText';
import { createOptionsTree, filterSelectedStudents } from './utils';
import withQuestionsTypesOnAssignment from 'legacy-src/utility/withQuestionsTypesOnAssignment';
import PopOver from 'chameleon/ui-stack/ui/base/popover';
import ConfirmDialog from 'chameleon/ui-stack/ui/modals/confirm-dialog';

import './index.scss';

const AnswerSheetsPreviewLoadingLabel = (
  <div>
    <LoadingSpinner height={60} />
    Generating answer sheet preview
  </div>
);

const renderCheckboxItem = ({ input, studentObj, checked }) => (
  <FormControlLabel
    control={
      <Checkbox
        checked={checked}
        onChange={input.onChange}
        value={`${studentObj.label}`}
      />
    }
    label={
      <span className="checkbox-list__student-item-label">
        {studentObj.label}
      </span>
    }
  />
);

// preventing the default refresh on forms
// we are going to handle submit through the button directly
const handleSubmit = (event) => event.preventDefault();

const GenerateAnswerSheetModal = ({
  answerSheetsPreviewUrl,
  assignmentId,
  assignmentPaperscorerAnswerSheet,
  assignmentTitle,
  className,
  dispatch,
  formValues,
  frqPrintSettings,
  generateAnswerSheets,
  questions,
  generateAnswerSheetsPreview,
  loadingAnswerSheetsPreview,
  params,
  reset,
  resetAnswerSheetsPreview,
  sectionName,
  selectAllStudents,
  students,
  subjectName,
  closePopUp,
  setModalOpen,
  isGraphQLLoading: questionTypesOnAssignmentLoading,
  userData,
  PSShowTipsFlag,
}) => {
  /**
   * The component is going to load it's own props instead of having them passed down
   */
  if (
    [null, undefined].includes(questions) ||
    ([null, undefined].includes(assignmentPaperscorerAnswerSheet) &&
      questionTypesOnAssignmentLoading)
  ) {
    return <LoadingSpinner height={60} />;
  }

  const [answerSheetStep, setAnswerSheetStep] = useState(0);
  const [fetchedFrqPrintSettings, setFetchedFrqPrintSettings] = useState(null);
  const [showStep3Tips, setShowStep3Tips] = useState(false);
  const [showStep3TipsCheck, setShowStep3TipsCheck] = useState(false);

  const showStep3TipsPreferenceKey = `${userData?.me?.initId}-PS-Tips`;
  const TipFormURL =
    'https://media.academicmerit.com/apclassroom/AP_Classroom_assessment_scanning_tips.pdf';
  const paperscorerAnswerSheetObj = JSON.parse(
    assignmentPaperscorerAnswerSheet,
  );
  const {
    json_data: { questions: assignmentQuestionSettings },
  } = paperscorerAnswerSheetObj;
  // frqs are identified by type number 9 by paperscorer
  const hasFrqs = some(assignmentQuestionSettings, ['type', 9]);
  // ensure we only pass down frqs to the printSettings component as we don't configure mcqs
  const frqs =
    hasFrqs &&
    questions.filter(
      (question) => question.__typename === 'FreeResponseQuestion',
    );
  const assessmentName = get(
    JSON.parse(assignmentPaperscorerAnswerSheet),
    'json_data.ltst',
    '',
  );
  const showRefresh = fetchedFrqPrintSettings !== frqPrintSettings && !!hasFrqs;
  const assignmentProgressLink = `/${params.subject}/assignments/progress/${assignmentId}`;
  const studentsAreSelected =
    formValues &&
    filterSelectedStudents({ students: formValues.students }).length > 0;
  const canRenderPaperscorerAnswerSheets =
    paperscorerAnswerSheetObj.can_render_paperscore_answer_sheet;

  useEffect(() => {
    if (
      PSShowTipsFlag &&
      !window.localStorage.getItem(showStep3TipsPreferenceKey)
    ) {
      setShowStep3Tips(true);
    }
    if (!hasFrqs && canRenderPaperscorerAnswerSheets) {
      generateAnswerSheetsPreview({
        assignmentPaperscorerAnswerSheet,
      });
    }
    // destroy frqPrintSettings form state if it's present and the generateAS modal is not open
    // to avoid using the same settings for another assignment
    return () => {
      if (!isEmpty(frqPrintSettings)) {
        dispatch(destroy('frqPrintSettings'));
      }
      dispatch(destroy('answerSheetsPreviewUrl'));
    };
  }, []);

  const fetchPreviewPDF = () => {
    generateAnswerSheetsPreview({
      assignmentPaperscorerAnswerSheet,
      frqPrintSettings,
    });
    setFetchedFrqPrintSettings(frqPrintSettings);
  };

  const generateAnswerSheetSubmit = () => {
    // set showStep3TipsCheck to localStorage
    if (showStep3TipsCheck) {
      window.localStorage.setItem(showStep3TipsPreferenceKey, true);
    }
    let studentsSelectedByTeacher = filterSelectedStudents({
      students: formValues.students,
    });
    generateAnswerSheets({
      assignmentPaperscorerAnswerSheet,
      ...formValues,
      students: studentsSelectedByTeacher,
      frqPrintSettings,
      assignmentName: assignmentTitle,
      sectionName,
      subjectName,
      assignmentProgressLink,
      assignmentId,
    });
    // destroy frqPrintSettings form state if it's present after generating an answersheet
    // to avoid using the same settings for another assignment
    !isEmpty(frqPrintSettings) && dispatch(destroy('frqPrintSettings'));
    setModalOpen(false);
    !isNil(closePopUp) && closePopUp();
    toast(
      <span>
        We’re generating your answer sheets, which can take a minute. We will
        notify you when they are ready.
      </span>,
      { timeout: 15 },
    );
  };

  const toggleshowStep3Tips = () => {
    setShowStep3TipsCheck(!showStep3TipsCheck);
  };

  const onCloseCallback = () => {
    reset();
    resetAnswerSheetsPreview();
    setModalOpen(false);
    !isNil(closePopUp) && closePopUp();
  };
  let contentButtons = {};
  if (canRenderPaperscorerAnswerSheets) {
    contentButtons = {
      content: {
        btnPrimary: showRefresh
          ? !fetchedFrqPrintSettings
            ? 'Preview'
            : 'Refresh Preview'
          : 'Next',
        btnSecondary: 'Cancel',
      },
      onSuccess: () => {
        showRefresh
          ? fetchPreviewPDF()
          : setAnswerSheetStep(answerSheetStep + 1);
      },
      onSecondaryBtnClick: onCloseCallback,
    };
  } else {
    contentButtons = {
      content: {
        btnPrimary: 'Close',
      },
      onSuccess: onCloseCallback,
    };
  }

  // each step of answer sheet changes the modal's props
  const MODAL_PROPS_MAP = [
    {
      ...contentButtons,
      children: () => (
        <Fragment>
          <div
            className={cn('GenerateASModal__preview-title', {
              'GenerateASModal__preview-title--warning':
                !canRenderPaperscorerAnswerSheets,
            })}
          >
            <DownloadAnswerSheetTitle
              hasFrqs={hasFrqs}
              assessmentName={assessmentName}
              query={testBookletQuery}
              variables={{ assignmentId, masterSubjectId: params.subject }}
              getURLFromResponse={(resp) => resp.data.assignment.testBookletUrl}
              className="GenerateASModal__view-test-books"
              displayTittle={canRenderPaperscorerAnswerSheets}
              btn={{ style: 'minimal' }}
            />
          </div>
          <div className="GenerateASModal__preview-wrapper">
            {!canRenderPaperscorerAnswerSheets ? (
              <div
                className={cn('GenerateASModal__preview', {
                  'GenerateASModal__preview--warning':
                    !canRenderPaperscorerAnswerSheets,
                })}
              >
                <div className="GenerateASModal__warning-wrapper">
                  <div className="GenerateASModal__warning-icon-wrapper">
                    <LegacyIcon
                      name="filled_circle_info"
                      width={20}
                      height={20}
                    />
                  </div>
                  <div className="GenerateASModal__warning-text-wrapper">
                    <span className="GenerateASModal__warning-title">
                      Acceptable Answer Sheet Response Formats
                    </span>
                    <div className="GenerateASModal__info-text-wrapper">
                      <p>
                        This quiz includes questions that aren't supported on
                        print answer sheets, such as audio, video, or file
                        upload.
                      </p>
                      <p className="GenerateASModal__info-text-wrapper--second-paragragh">
                        To print answer sheets for multiple-choice and written
                        free-response questions, please first remove any
                        questions that ask students to record audio responses or
                        submit videos or other files.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : hasFrqs ? (
              <Fragment>
                {loadingAnswerSheetsPreview && (
                  <PopOver
                    className="GenerateASModal__loading"
                    defaultOpen
                    trigger={AnswerSheetsPreviewLoadingLabel}
                  />
                )}
                <div className="GenerateASModal__preview">
                  <FRQPrintSettings
                    assignmentQuestionSettings={assignmentQuestionSettings}
                    questions={frqs}
                  />
                  {answerSheetsPreviewUrl && (
                    <embed
                      src={answerSheetsPreviewUrl}
                      type="application/pdf"
                      className="GenerateASModal__pdf-preview"
                    />
                  )}
                </div>
              </Fragment>
            ) : (
              <div className="GenerateASModal__preview--MCQ-only">
                {loadingAnswerSheetsPreview && (
                  <PopOver
                    className="GenerateASModal__loading"
                    defaultOpen
                    trigger={AnswerSheetsPreviewLoadingLabel}
                  />
                )}
                {answerSheetsPreviewUrl && (
                  <embed
                    src={answerSheetsPreviewUrl}
                    type="application/pdf"
                    className="GenerateASModal__pdf-preview"
                  />
                )}
              </div>
            )}
          </div>
        </Fragment>
      ),
    },
    {
      content: {
        btnPrimary:
          PSShowTipsFlag && showStep3Tips ? 'Next' : 'Generate Answer Sheets',
        btnSecondary: 'Back',
      },
      onSuccess: () => {
        PSShowTipsFlag && showStep3Tips
          ? setAnswerSheetStep(answerSheetStep + 1)
          : generateAnswerSheetSubmit();
      },
      onSecondaryBtnClick: () => {
        setAnswerSheetStep(0);
      },
      children: () => (
        <div className="h-full">
          <div className="GenerateASModal__selector-description text-left">
            Select which students will receive an answer sheet:
          </div>
          <div className="GenerateASModal__selector-btns text-left">
            <ClickableText
              onClick={() =>
                dispatch(
                  change('generateAnswerSheets', 'students', selectAllStudents),
                )
              }
            >
              Select All
            </ClickableText>
            <ClickableText
              onClick={() =>
                dispatch(change('generateAnswerSheets', 'students', students))
              }
            >
              Clear
            </ClickableText>
          </div>
          <div className="checkbox-list text-left">
            <ul className="checkbox-list__list">
              <li className="checkbox-list__list-item">
                <span className="checkbox-list__section-label">
                  {sectionName}
                </span>
                <ul className="checkbox-list__list">
                  <FormSection name="students">
                    {Object.keys(formValues.students).map((studentId) => {
                      const studentObj = formValues.students[studentId];
                      return (
                        <li
                          key={studentId}
                          className="checkbox-list__student-item"
                        >
                          <Field
                            name={`${studentId}.value`}
                            component={renderCheckboxItem}
                            props={{ studentObj, checked: studentObj.value }}
                          />
                        </li>
                      );
                    })}
                  </FormSection>
                </ul>
              </li>
            </ul>
          </div>
          {PSShowTipsFlag && !showStep3Tips && (
            <dev className="GenerateASModal__tips-description flex text-left">
              <Icon
                id="icon_filled"
                ariaLabel="Info Icon"
                className="GenerateASModal__tips-icon"
              />
              <Link target={'_blank'} to={TipFormURL}>
                Tips
              </Link>
              <span className="ml-1">
                {' '}
                for your answer sheets to be successfully processed
              </span>
            </dev>
          )}
        </div>
      ),
    },
    {
      content: {
        btnPrimary: 'Generate Answer Sheets',
        btnSecondary: 'Back',
      },
      onSuccess: () => {
        generateAnswerSheetSubmit();
      },
      onSecondaryBtnClick: () => {
        setAnswerSheetStep(1);
      },
      children: () => (
        <div className="GenerateASModal__step3-tips-wrapper">
          <div className="GenerateASModal__step3-tips-description text-left">
            Review these tips to print, scan, and upload answer sheets for
            successful processing.
          </div>
          <embed
            src={TipFormURL}
            type="application/pdf"
            className="GenerateASModal__pdf-preview"
          />
          <div className="GenerateASModal__step3-checkbox">
            <FormControlLabel
              control={
                <Checkbox
                  checked={showStep3TipsCheck}
                  onChange={toggleshowStep3Tips}
                  value={showStep3TipsCheck}
                />
              }
              label={
                <span className="checkbox-list__student-item-label">
                  Do not show Step 3 again
                </span>
              }
            />
          </div>
        </div>
      ),
    },
  ];

  const activeClassName = 'font-bold text-black';
  const iconInactiveClassName = 'fill-current text-gray-500';

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      className={`GenerateASModal ${className || ''}`}
    >
      <ConfirmDialog
        size="huge"
        wrapperClassName="GenerateASModal GenerateASModal__modal h-screenmodal bg-gray-200"
        contentClassName="h-full"
        headingClassName="text-gray-500"
        title={
          <Fragment>
            <span
              className={cn({
                [activeClassName]: answerSheetStep === 0,
              })}
            >
              Step 1: Format the Answer Sheet
            </span>
            <LegacyIcon
              className={iconInactiveClassName}
              name="chevron_right"
              size="12px"
            />
            <span
              className={cn({
                [activeClassName]: answerSheetStep === 1,
              })}
            >
              Step 2: Select Students
            </span>
            {showStep3Tips && (
              <span>
                <LegacyIcon
                  className={iconInactiveClassName}
                  name="chevron_right"
                  size="12px"
                />
                <span
                  className={cn({
                    [activeClassName]: answerSheetStep === 2,
                  })}
                >
                  Step 3: Tips for Successful Answer Sheet Processing
                </span>
              </span>
            )}
          </Fragment>
        }
        isPrimaryBtnDisabled={!studentsAreSelected}
        onClose={() => {
          setAnswerSheetStep(0);
          reset();
          resetAnswerSheetsPreview();
          setModalOpen(false);
          !isNil(closePopUp) && closePopUp();
        }}
        {...MODAL_PROPS_MAP[answerSheetStep]}
      >
        <form className="GenerateASModal__form" onSubmit={handleSubmit}>
          {MODAL_PROPS_MAP[answerSheetStep].children()}
        </form>
      </ConfirmDialog>
    </div>
  );
};

GenerateAnswerSheetModal.propTypes = {
  /**
   * This prop was added because this component was receiving props it needs in some instances
   * but not in others, in the former we don't want it to make a GQL call it doesn't need i.e.
   * withQuestionsTypesOnAssignment. However, in the latter case, we would pass this prop to be
   * false and ensure it makes the GQL call to get the props it requires
   */
  skipQuestionTypeQuery: PropTypes.bool.isRequired,
};

const selector = getFormValues('generateAnswerSheets');
const frqsettingsSelector = formValueSelector('frqPrintSettings');

export default compose(
  connect(
    (state, props) => {
      const submissions = get(
        state,
        'teacherAssignment.submissionData.submissions',
      );
      const studentsInSection = !isNil(props.sectionStudents)
        ? props.sectionStudents
        : submissions;
      const students = createOptionsTree({ students: studentsInSection });
      const selectAllStudents = createOptionsTree({
        students: studentsInSection,
        value: true,
      });
      return {
        initialValues: {
          students: selectAllStudents,
          answerSheetTitle: '',
        },
        students,
        selectAllStudents,
        formValues: selector(state),
        sectionName:
          props.sectionName ||
          get(state, 'teacherAssignment.assignment.section_name', ''),
        frqPrintSettings: frqsettingsSelector(state, 'frqSettings'),
        answerSheetsPreviewUrl: get(
          state,
          'paperscorer.answerSheetsPreviewUrl',
          '',
        ),
        loadingAnswerSheetsPreview: get(
          state,
          'paperscorer.loadingAnswerSheetsPreview',
          false,
        ),
        assignmentTitle:
          props.assignmentTitle ||
          get(state, 'teacherAssignment.assignment.item.title', ''),
        assignmentId:
          props.assignmentId ||
          get(state, 'teacherAssignment.assignment.id', 0),
        subjectName: props.subjectName || get(state, 'subject.data.name', ''),
        /**
         * Added to make sure withQuestionsTypesOnAssignment receives the correct prop
         * value
         */
        skipQuestionTypeQuery: props.skipQuestionTypeQuery,
        PSShowTipsFlag: get(state, 'featureFlag.PS_show_tips', false),
      };
    },
    {
      generateAnswerSheets: generateASAction,
      generateAnswerSheetsPreview: generateAnswerSheetsPreviewAction,
      resetAnswerSheetsPreview: resetAnswerSheetsPreviewAction,
    },
  ),
  withQuestionsTypesOnAssignment,
  reduxForm({
    form: 'generateAnswerSheets',
  }),
)(GenerateAnswerSheetModal);
